/*
  MyProjectsPanel - Przywrócono funkcję duplikowania rzutów:
  - Dodano przycisk FiCopy do każdego kafli rzutu.
  - Implementacja handleDuplicateProject wywołująca logikę w ProjectManager.
*/
import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import projectManager from '../core/ProjectManager';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';
import useDialogStore from '../store/useDialogStore';
import useNotificationStore from '../store/useNotificationStore';

const { FiX, FiFolder, FiTrash2, FiFolderPlus, FiCopy } = FiIcons;

const MyProjectsPanel = ({ isOpen, onClose }) => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { openDialog } = useDialogStore();
  const { addNotification } = useNotificationStore();

  const loadProjects = async () => {
    setIsLoading(true);
    const result = await projectManager.getProjects();
    setProjects(result?.data ?? []);
    setIsLoading(false);

    if (!result?.success) {
      addNotification('error', result?.error || 'Nie udało się pobrać projektów.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  const handleOpenProject = (id) => {
    openDialog({
      title: 'Otwórz Projekt',
      message: 'Czy na pewno chcesz otworzyć ten projekt? Niezapisane zmiany w obecnym projekcie zostaną utracone.',
      confirmText: 'Otwórz',
      variant: 'default',
      onConfirm: async () => {
        if (await projectManager.loadProjectFromDatabase(id)) {
          onClose();
          addNotification('success', 'Projekt został wczytany.');
        } else {
          addNotification(
            'error',
            projectManager.getLastError() || 'Nie udało się wczytać projektu.'
          );
        }
      }
    });
  };

  const handleDeleteProject = (e, id) => {
    e.stopPropagation();
    openDialog({
      title: 'Usuń Projekt',
      message: 'Czy na pewno chcesz trwale usunąć ten projekt?',
      confirmText: 'Usuń',
      variant: 'danger',
      onConfirm: async () => {
        if (await projectManager.deleteProject(id)) {
          await loadProjects();
          addNotification('info', 'Projekt został usunięty.');
        } else {
          addNotification('error', 'Nie udało się usunąć projektu.');
        }
      }
    });
  };

  const handleDuplicateProject = async (e, id) => {
    e.stopPropagation();
    if (await projectManager.duplicateProject(id)) {
      await loadProjects();
      addNotification('success', 'Projekt został zduplikowany.');
    } else {
      addNotification('error', 'Błąd podczas duplikowania projektu.');
    }
  };

  const handleNewProject = () => {
    openDialog({
      title: 'Nowy Projekt',
      message: 'Czy na pewno chcesz utworzyć nowy projekt?',
      confirmText: 'Utwórz nowy',
      variant: 'default',
      onConfirm: () => {
        projectManager.newProject();
        onClose();
        addNotification('success', 'Nowy projekt utworzony.');
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-4xl h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
             <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiFolder} className="text-indigo-600 text-2xl" />
             </div>
             <div>
                <h2 className="text-2xl font-bold text-gray-900">Moje Rzuty</h2>
                <p className="text-sm text-gray-500">Zarządzaj zapisanymi projektami</p>
             </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-400 text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p className="text-lg font-medium">Pobieranie projektów...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <SafeIcon icon={FiFolder} className="text-6xl mb-4 opacity-20" />
              <p className="text-lg font-medium">Brak zapisanych projektów</p>
              <p className="text-sm">Zapisz swój pierwszy projekt, aby zobaczyć go tutaj.</p>
              <button 
                onClick={handleNewProject}
                className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                 <SafeIcon icon={FiFolderPlus} />
                 Utwórz nowy projekt
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div 
                  key={project?.id}
                  onClick={() => handleOpenProject(project?.id)}
                  className="group bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-lg transition-all cursor-pointer overflow-hidden flex flex-col"
                >
                  <div className="aspect-video bg-gray-100 relative border-b border-gray-100">
                    {project?.thumbnail ? (
                      <img src={project.thumbnail} alt={project?.name ?? 'Projekt'} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <SafeIcon icon={FiFolder} className="text-4xl" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                  </div>

                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-bold text-gray-900 mb-1 truncate">{project?.name ?? 'Bez nazwy'}</h3>
                    <p className="text-xs text-gray-500 mb-4">
                      Zapisano: {project?.updatedAt ? formatDistanceToNow(project.updatedAt, { addSuffix: true, locale: pl }) : 'Nieznana data'}
                    </p>
                    
                    <div className="mt-auto flex items-center gap-2">
                      <button 
                        className="flex-1 py-2 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-100 transition-colors"
                      >
                        Otwórz
                      </button>
                      <button 
                        onClick={(e) => handleDuplicateProject(e, project?.id)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Duplikuj projekt"
                      >
                        <SafeIcon icon={FiCopy} />
                      </button>
                      <button 
                        onClick={(e) => handleDeleteProject(e, project?.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Usuń projekt"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default MyProjectsPanel;